// src/components/RenderList.js
import React from "react";

export function ResearchBlock({ data }) {
  return (
    <div>
      {data.blurb && <p style={{ marginBottom: "1rem" }}>{data.blurb}</p>}
      <ul style={{ paddingLeft: "1.25rem" }}>
        {data.items.map((it, i) => (
          <li key={i} style={{ marginBottom: "1rem" }}>
            {it.heading ? <strong>{it.heading}: </strong> : null}
            {it.text}
            {Array.isArray(it.subtopics) && it.subtopics.length > 0 && (
              <ul style={{ marginTop: ".25rem", paddingLeft: "1.25rem" }}>
                {it.subtopics.map((s, j) => (
                  <li key={j} style={{ marginBottom: ".2rem", color: "#555" }}>{s}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ProjectsBlock({ data }) {
  return (
    <div>
      <ul style={{ paddingLeft: 0, listStyle: "none" }}>
        {data.items.map((p, i) => (
          <li
            key={i}
            style={{
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "flex-start",
              gap: "1rem"
            }}
          >
            {p.logo ? (
              <img
                src={p.logo}
                alt={`${p.name} logo`}
                className="project-logo"
              />
            ) : (
              <div className="project-logo-placeholder" />
            )}

            <div>
              <div style={{ fontWeight: 600 }}>
                {p.name}
                {p.period && (
                  <span style={{ color: "#666", fontWeight: 400 }}>
                    {" "}— {p.period}
                  </span>
                )}
              </div>

              <div style={{ margin: ".25rem 0" }}>{p.summary}</div>

              {Array.isArray(p.links) && p.links.length > 0 && (
                <div style={{ fontSize: ".95rem" }}>
                  {p.links.map((l, j) => (
                    <span key={j}>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={l.url.toLowerCase().endsWith(".pdf") ? "" : undefined}
                      >
                        {l.label}
                      </a>
                      {j < p.links.length - 1 ? " · " : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EducationList({ data }) {
    const items = data?.items ?? [];
    return (
      <ul style={{ paddingLeft: 0, listStyle: "none" }}>
        {items.map((e, i) => (
          <li key={i} style={{ marginBottom: "1.5rem", display: "flex", alignItems: "flex-start", gap: "1rem" }}>
            {e.logo && (
              <img
                src={e.logo}
                alt={`${e.school} logo`}
                style={{ width: "48px", height: "48px", objectFit: "contain" }}
              />
            )}
            <div>
              <div style={{ fontWeight: 600 }}>{e.degree}</div>
              <div style={{ color: "#555" }}>
                {e.school}{e.location ? ` — ${e.location}` : ""}{e.date ? ` • ${e.date}` : ""}
              </div>
              {Array.isArray(e.details) && (
                <ul style={{ marginTop: ".4rem", paddingLeft: "1.25rem" }}>
                  {e.details.map((d, j) => <li key={j}>{d}</li>)}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  }
  
  export function ExperienceList({ data }) {
    const items = data?.items ?? [];
    return (
      <ul style={{ paddingLeft: 0, listStyle: "none" }}>
        {items.map((x, i) => (
          <li key={i} style={{ marginBottom: "1.5rem", display: "flex", alignItems: "flex-start", gap: "1rem" }}>
            {x.logo && (
              <img
                src={x.logo}
                alt={`${x.company} logo`}
                style={{ width: x.logoSize || "48px", height: x.logoSize || "48px", objectFit: "contain" }}
              />
            )}
            <div>
              <div style={{ fontWeight: 600 }}>{x.role} — {x.company}</div>
              <div style={{ color: "#555" }}>
                {x.location}{x.date && x.location ? " • " : ""}{x.date}
              </div>
              {Array.isArray(x.bullets) && (
                <ul style={{ marginTop: ".4rem", paddingLeft: "1.25rem" }}>
                  {x.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  export function TalksList({ data }) {
    const items = Array.isArray(data) ? data : [];
    return (
      <ul style={{ paddingLeft: "1.25rem" }}>
        {items.map((t, i) => (
          <li key={i} style={{ marginBottom: ".75rem" }}>
            <strong>{t.title}</strong>
            {t.host ? `, ${t.host}` : ""}
            {t.date ? ` (${t.date})` : ""}
            {t.note ? <span style={{ color: "#888", fontStyle: "italic" }}> — {t.note}</span> : ""}
          </li>
        ))}
        {items.length === 0 && <li style={{ color: "#666" }}>No talks yet.</li>}
      </ul>
    );
  }

  export function PublicationsList({ data }) {
    const items = Array.isArray(data) ? data : [];
    return (
      <ul style={{ paddingLeft: "1.25rem" }}>
        {items.map((p, i) => (
          <li key={i} style={{ marginBottom: ".75rem" }}>
            {p.url ? (
              <a href={p.url} target="_blank" rel="noopener noreferrer"><strong>{p.title}</strong></a>
            ) : (
              <strong>{p.title}</strong>
            )}
            {Array.isArray(p.authors) && p.authors.length > 0 ? `. ${p.authors.join(", ")}` : ""}
            {p.venue ? `. ${p.venue}` : ""}
            {p.note ? <span style={{ color: "#888", fontStyle: "italic" }}> — {p.note}</span> : ""}
            {p.description ? <div style={{ marginTop: ".25rem", color: "#555" }}>{p.description}</div> : ""}
          </li>
        ))}
        {items.length === 0 && <li style={{ color: "#666" }}>No publications yet.</li>}
      </ul>
    );
  }

  export function MiscList({ data }) {
    const items = data?.items ?? [];
    return (
      <ul style={{ paddingLeft: "1.25rem" }}>
        {items.map((m, i) => (
          <li key={i} style={{ marginBottom: "1rem" }}>
            {m.heading ? <strong>{m.heading}: </strong> : null}
            {m.text}
          </li>
        ))}
        {items.length === 0 && <li style={{color:"#666"}}>No miscellaneous items yet.</li>}
      </ul>
    );
  }
  
  